import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { DiagnosticFormData } from '../types/diagnostic';
import { calculateSecurityScore } from '../utils/securityCalculator';
import { generateRecommendedActions } from '../services/recommendationService';
import { createDiagnostic, getDiagnosticById } from '../services/diagnosticService';
import { bulkCreateActionsFromRecommendations, createInitialSnapshot } from '../services/progressTrackingService';
import toast from 'react-hot-toast';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { dpsTheme } from '../styles/dpsTheme';
import { cleanupPendingDiagnostic } from '../utils/pendingDiagnosticStorage';

export default function Checkout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const formData = location.state?.formData as DiagnosticFormData;
  const existingDiagnosticId = location.state?.diagnosticId as string | undefined;
  const [isLoading, setIsLoading] = useState(false);
  const [loadedFormData, setLoadedFormData] = useState<DiagnosticFormData | null>(formData || null);
  const [isLoadingDiagnostic, setIsLoadingDiagnostic] = useState(false);

  useEffect(() => {
    async function loadDiagnosticIfNeeded() {
      if (!currentUser) {
        toast.error('Veuillez vous connecter pour continuer');
        navigate('/login', { state: { from: '/checkout', formData, diagnosticId: existingDiagnosticId } });
        return;
      }

      if (!formData && existingDiagnosticId) {
        setIsLoadingDiagnostic(true);
        try {
          const diagnostic = await getDiagnosticById(existingDiagnosticId);
          if (diagnostic && diagnostic.diagnostic_data) {
            setLoadedFormData(diagnostic.diagnostic_data);
            cleanupPendingDiagnostic();
          } else {
            toast.error('Impossible de charger le diagnostic');
            navigate('/diagnostic');
          }
        } catch (error) {
          console.error('Error loading diagnostic:', error);
          toast.error('Erreur lors du chargement du diagnostic');
          navigate('/diagnostic');
        } finally {
          setIsLoadingDiagnostic(false);
        }
      } else if (!formData && !existingDiagnosticId) {
        navigate('/diagnostic');
      }
    }

    loadDiagnosticIfNeeded();
  }, [formData, existingDiagnosticId, currentUser, navigate]);

  const handleCheckout = async () => {
    const dataToUse = loadedFormData || formData;
    if (!currentUser || !dataToUse) return;

    try {
      setIsLoading(true);

      let diagnosticId = existingDiagnosticId;

      if (!diagnosticId) {
        const scoreData = calculateSecurityScore(dataToUse);
        const recommendedActions = generateRecommendedActions(dataToUse, scoreData.categoryScores);

        const diagnostic = await createDiagnostic(
          currentUser.id,
          dataToUse,
          scoreData.score,
          scoreData.categoryScores.protection,
          scoreData.categoryScores.surveillance,
          scoreData.categoryScores.deterrence,
          recommendedActions
        );

        if (!diagnostic || !diagnostic.id) {
          throw new Error('Failed to create diagnostic');
        }

        await createInitialSnapshot(
          diagnostic.id,
          scoreData.score,
          scoreData.categoryScores.protection,
          scoreData.categoryScores.surveillance,
          scoreData.categoryScores.deterrence
        );

        await bulkCreateActionsFromRecommendations(
          diagnostic.id,
          recommendedActions.map(action => ({
            id: action.id,
            title: action.title,
            category: action.category as 'protection' | 'surveillance' | 'dissuasion',
            priority: action.priority as 'high' | 'medium' | 'low',
            estimatedCost: action.estimatedCost,
            scoreImpact: action.scoreImpact,
            description: action.description
          }))
        );

        diagnosticId = diagnostic.id;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error('Configuration error: missing Supabase credentials');
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({
          diagnosticId: diagnosticId,
          userId: currentUser.id,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Checkout session error:', errorData);
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const { url } = await response.json();

      if (!url) {
        throw new Error('No checkout URL returned from server');
      }

      window.location.href = url;
    } catch (error) {
      console.error('Error during checkout:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erreur lors du paiement';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (!currentUser) {
    return null;
  }

  if (isLoadingDiagnostic) {
    return (
      <div style={{
        minHeight: '100vh',
        background: dpsTheme.colors.background.primary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center', color: dpsTheme.colors.text.primary }}>
          <div style={{ fontSize: '24px', marginBottom: '16px' }}>Chargement de votre diagnostic...</div>
        </div>
      </div>
    );
  }

  const dataToUse = loadedFormData || formData;
  if (!dataToUse) {
    return null;
  }

  const { score } = calculateSecurityScore(dataToUse);

  return (
    <div style={{
      minHeight: '100vh',
      background: dpsTheme.colors.background.primary,
      paddingTop: '80px',
      paddingBottom: '60px'
    }}>
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: dpsTheme.spacing['3xl'] }}>
        <button
          onClick={() => navigate('/results', { state: { formData: dataToUse } })}
          style={{
            marginBottom: dpsTheme.spacing.xl,
            display: 'inline-flex',
            alignItems: 'center',
            gap: dpsTheme.spacing.sm,
            color: dpsTheme.colors.text.secondary,
            fontSize: dpsTheme.typography.fontSize.base,
            fontWeight: dpsTheme.typography.fontWeight.medium,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = dpsTheme.colors.text.primary;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = dpsTheme.colors.text.secondary;
          }}
        >
          <ArrowLeftIcon style={{ width: '20px', height: '20px' }} />
          Retour aux résultats
        </button>

        <div style={{
          background: dpsTheme.colors.background.card,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${dpsTheme.colors.border.primary}`,
          borderRadius: dpsTheme.borderRadius.lg,
          overflow: 'hidden',
          boxShadow: dpsTheme.shadows.card
        }}>
          <div style={{
            background: dpsTheme.colors.accent.blue.gradient,
            padding: dpsTheme.spacing['3xl']
          }}>
            <h1 style={{
              fontSize: dpsTheme.typography.fontSize['4xl'],
              fontWeight: dpsTheme.typography.fontWeight.bold,
              color: '#ffffff',
              marginBottom: dpsTheme.spacing.sm
            }}>
              Rapport DPS Premium
            </h1>
            <p style={{
              fontSize: dpsTheme.typography.fontSize.lg,
              color: 'rgba(255, 255, 255, 0.9)'
            }}>
              Diagnostic de Performance Sécuritaire complet
            </p>
          </div>

          <div style={{ padding: dpsTheme.spacing['3xl'] }}>
            <div style={{ marginBottom: dpsTheme.spacing['2xl'] }}>
              <h2 style={{
                fontSize: dpsTheme.typography.fontSize['2xl'],
                fontWeight: dpsTheme.typography.fontWeight.semibold,
                color: dpsTheme.colors.text.primary,
                marginBottom: dpsTheme.spacing.lg
              }}>
                Récapitulatif de votre diagnostic
              </h2>
              <div style={{
                background: dpsTheme.colors.background.darker,
                borderRadius: dpsTheme.borderRadius.md,
                padding: dpsTheme.spacing.lg,
                display: 'flex',
                flexDirection: 'column',
                gap: dpsTheme.spacing.md
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: dpsTheme.colors.text.muted, fontSize: dpsTheme.typography.fontSize.base }}>
                    Type de logement:
                  </span>
                  <span style={{
                    fontWeight: dpsTheme.typography.fontWeight.semibold,
                    color: dpsTheme.colors.text.primary,
                    fontSize: dpsTheme.typography.fontSize.base
                  }}>
                    {dataToUse.housingType === 'house' ? 'Maison' : 'Appartement'}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: dpsTheme.colors.text.muted, fontSize: dpsTheme.typography.fontSize.base }}>
                    Adresse:
                  </span>
                  <span style={{
                    fontWeight: dpsTheme.typography.fontWeight.semibold,
                    color: dpsTheme.colors.text.primary,
                    fontSize: dpsTheme.typography.fontSize.base
                  }}>
                    {dataToUse.city}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: dpsTheme.colors.text.muted, fontSize: dpsTheme.typography.fontSize.base }}>
                    Score de sécurité:
                  </span>
                  <span style={{
                    fontWeight: dpsTheme.typography.fontWeight.bold,
                    fontSize: dpsTheme.typography.fontSize['3xl'],
                    color: dpsTheme.colors.accent.blue.primary
                  }}>
                    {score}/100
                  </span>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: dpsTheme.spacing['2xl'] }}>
              <h2 style={{
                fontSize: dpsTheme.typography.fontSize['2xl'],
                fontWeight: dpsTheme.typography.fontWeight.semibold,
                color: dpsTheme.colors.text.primary,
                marginBottom: dpsTheme.spacing.lg
              }}>
                Ce que vous allez recevoir
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: dpsTheme.spacing.md }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: dpsTheme.spacing.md }}>
                  <svg style={{ width: '24px', height: '24px', color: dpsTheme.colors.accent.green.primary, flexShrink: 0 }} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <div style={{ fontWeight: dpsTheme.typography.fontWeight.semibold, color: dpsTheme.colors.text.primary }}>
                      Analyse détaillée par catégories
                    </div>
                    <div style={{ fontSize: dpsTheme.typography.fontSize.sm, color: dpsTheme.colors.text.muted }}>
                      Protection physique, Surveillance, Dissuasion
                    </div>
                  </div>
                </div>
{[
                  { title: 'Cartographie des vulnérabilités', desc: 'Identification des 3 points faibles principaux' },
                  { title: 'Recommandations chiffrées', desc: 'Actions priorisées avec coûts estimés et impacts' },
                  { title: 'Simulateur d\'améliorations', desc: 'Projetez l\'impact de vos futurs investissements' },
                  { title: 'Comparaison anonyme', desc: 'Positionnement par rapport aux autres diagnostics' },
                  { title: 'Analyse contextuelle IA', desc: 'Insights personnalisés générés par intelligence artificielle' }
                ].map((feature, index) => (
                  <div key={index} style={{ display: 'flex', alignItems: 'flex-start', gap: dpsTheme.spacing.md }}>
                    <svg style={{ width: '24px', height: '24px', color: dpsTheme.colors.accent.green.primary, flexShrink: 0 }} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <div style={{ fontWeight: dpsTheme.typography.fontWeight.semibold, color: dpsTheme.colors.text.primary }}>
                        {feature.title}
                      </div>
                      <div style={{ fontSize: dpsTheme.typography.fontSize.sm, color: dpsTheme.colors.text.muted }}>
                        {feature.desc}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{
              borderTop: `1px solid ${dpsTheme.colors.border.secondary}`,
              paddingTop: dpsTheme.spacing.xl
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: dpsTheme.spacing.xl }}>
                <div>
                  <div style={{
                    fontSize: dpsTheme.typography.fontSize['3xl'],
                    fontWeight: dpsTheme.typography.fontWeight.bold,
                    color: dpsTheme.colors.text.primary
                  }}>
                    9,90 €
                  </div>
                  <div style={{
                    fontSize: dpsTheme.typography.fontSize.sm,
                    color: dpsTheme.colors.text.muted
                  }}>
                    Paiement unique • Accès à vie
                  </div>
                </div>
              </div>

              <button
                onClick={handleCheckout}
                disabled={isLoading}
                style={{
                  width: '100%',
                  background: isLoading ? dpsTheme.colors.background.darker : dpsTheme.colors.accent.blue.gradient,
                  color: '#ffffff',
                  padding: `${dpsTheme.spacing.lg} ${dpsTheme.spacing.xl}`,
                  borderRadius: dpsTheme.borderRadius.md,
                  fontSize: dpsTheme.typography.fontSize.lg,
                  fontWeight: dpsTheme.typography.fontWeight.bold,
                  border: 'none',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  opacity: isLoading ? 0.6 : 1,
                  boxShadow: isLoading ? 'none' : dpsTheme.shadows.button
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.boxShadow = dpsTheme.shadows.hover;
                    e.currentTarget.style.transform = 'scale(1.02)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.boxShadow = dpsTheme.shadows.button;
                    e.currentTarget.style.transform = 'scale(1)';
                  }
                }}
              >
                {isLoading ? 'Redirection vers le paiement...' : 'Procéder au paiement sécurisé'}
              </button>

              <div style={{
                marginTop: dpsTheme.spacing.lg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: dpsTheme.spacing.sm,
                fontSize: dpsTheme.typography.fontSize.sm,
                color: dpsTheme.colors.text.muted
              }}>
                <svg style={{ width: '20px', height: '20px' }} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                Paiement 100% sécurisé par Stripe
              </div>
            </div>
          </div>
        </div>

        <div style={{
          marginTop: dpsTheme.spacing.xl,
          textAlign: 'center',
          fontSize: dpsTheme.typography.fontSize.sm,
          color: dpsTheme.colors.text.muted
        }}>
          <p>En procédant au paiement, vous acceptez nos conditions générales de vente.</p>
          <p style={{ marginTop: dpsTheme.spacing.sm }}>
            Votre rapport sera accessible immédiatement après validation du paiement.
          </p>
        </div>
      </div>
    </div>
  );
}
